import {
  UserAdministrationDto,
  BulkUserOperationType,
  BulkUserOperationData,
} from '@act/shared/data-transfer-objects';
import { PaginatedTable, PaginatedTableProps } from '@act/storybook';
import { useEffect, useMemo, useState, useRef } from 'react';
import { TextField, Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  getUsers,
  userAdminSelectors,
  bulkUserOperations,
  setFilters,
  resetFilters,
} from 'redux/slices/userAdminSlice';
import { getTeams, userAppSelectors } from 'redux/slices/usersSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { clientAppSelectors, getAllClients } from 'redux/slices/clientSlice';
import { IndexChildContainer } from 'react-components/member-index/indexContainer';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { useToast } from 'react-components/app/ReusableToast';
import { UserAdminTableColumns } from './UserAdminTableColumns';
import UserAdminFilters from './UserAdminFilters';
import { UserAdminFiltersType } from 'redux/types/userAdminTypes';
import { UserAdminFilterDrawer } from './UserAdminFilterDrawer';
import { AdminToolSkeleton } from '../AdminToolSkeleton';
import { BulkUserOperationsToolbar } from './BulkUserOperationsToolbar';

export const UserAdminTable = () => {
  const users = userAdminSelectors.useSelectAll();
  const allTeams = userAppSelectors.useSelectAllTeams();
  const allClients = clientAppSelectors.useSelectAllClients();
  const clientList = allClients.ids.map(id => allClients.entities[id]);
  const teamList = allTeams;
  const currentFilters = userAdminSelectors.useSelectFilters();
  const navigate = useAppNavigation();
  const toast = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isBulkOperationLoading, setIsBulkOperationLoading] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Track if we've initialized the form from persisted filters
  const hasInitializedForm = useRef(false);

  // Form for filters - lives in parent to maintain state
  // ControlledAutocomplete stores raw values (strings), not option objects
  const {
    control,
    getValues,
    reset: resetForm,
  } = useForm<{
    roles: string[];
    teamIds: string[];
    clientIds: string[];
    hasClient: string | null;
  }>({
    defaultValues: {
      roles: [],
      teamIds: [],
      clientIds: [],
      hasClient: null,
    },
  });

  // search by name filtering
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }
    return users.filter(user => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
  }, [users, searchTerm]);

  // Selection handlers
  const handleSelectUser = (userId: string, selected: boolean) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  };

  const handleClearSelection = () => {
    setSelectedUsers(new Set());
  };

  const isAllSelected =
    filteredData.length > 0 && selectedUsers.size === filteredData.length;
  const isIndeterminate =
    selectedUsers.size > 0 && selectedUsers.size < filteredData.length;

  const columns = useMemo(() => {
    return UserAdminTableColumns({
      allTeams,
      allClients: clientList,
      selectedUsers,
      onSelectUser: handleSelectUser,
    });
  }, [clientList, allTeams, selectedUsers, isAllSelected, isIndeterminate]);

  const USER_ADMIN_TABLE_PROPS: PaginatedTableProps<UserAdministrationDto> = {
    data: filteredData,
    inMemoryPagination: true,
    columnConfig: columns,
    onRowClick: row => rowCallback(row),
  };

  const rowCallback = (user: UserAdministrationDto) => {
    navigate.toUserCreationEdit(user.id);
  };

  const handleCreateUser = () => navigate.toUserCreationCreate();

  const handleBulkOperation = async (
    operation: BulkUserOperationType,
    data?: BulkUserOperationData
  ) => {
    setIsBulkOperationLoading(true);
    try {
      const userIds = Array.from(selectedUsers);

      const result = await dispatch(
        bulkUserOperations({
          body: {
            userIds,
            operation,
            data,
          },
        })
      );

      // Check if the operation was successful
      if (bulkUserOperations.fulfilled.match(result)) {
        const response = result.payload;

        // Show detailed success/error information
        if (response.successCount > 0 && response.failureCount === 0) {
          toast(
            `Successfully processed ${response.successCount} user(s)`,
            'success'
          );
        } else if (response.successCount > 0 && response.failureCount > 0) {
          toast(
            `Processed ${response.successCount} user(s) successfully, ${response.failureCount} failed. Check details for more information.`,
            'warning'
          );
        } else {
          toast(
            'All operations failed. Check details for more information.',
            'error'
          );
        }

        // Clear selection and refresh data
        setSelectedUsers(new Set());
        dispatch(getUsers({}));
      } else {
        // Handle rejected action
        const errorMessage = 'An error occurred during bulk operation';
        toast(errorMessage, 'error');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred during bulk operation';
      toast(errorMessage, 'error');
    } finally {
      setIsBulkOperationLoading(false);
    }
  };

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(getTeams({}));
    dispatch(getAllClients({}));
  }, [dispatch]);

  useEffect(() => {
    if (hasInitializedForm.current) {
      return;
    }

    if (
      !teamList ||
      teamList.length === 0 ||
      !clientList ||
      clientList.length === 0
    ) {
      return;
    }

    const formValues = {
      roles: currentFilters.roles || [],
      teamIds: currentFilters.teamIds || [],
      clientIds: currentFilters.clientIds || [],
      hasClient:
        currentFilters.hasClient !== undefined
          ? String(currentFilters.hasClient)
          : null,
    };

    resetForm(formValues);
    hasInitializedForm.current = true;
  }, [teamList, clientList]);

  useEffect(() => {
    const params: {
      roles?: string[];
      teamIds?: string[];
      clientIds?: string[];
      hasClient?: string;
    } = {};

    if (currentFilters.roles && currentFilters.roles.length > 0) {
      params.roles = currentFilters.roles;
    }
    if (currentFilters.teamIds && currentFilters.teamIds.length > 0) {
      params.teamIds = currentFilters.teamIds;
    }
    if (currentFilters.clientIds && currentFilters.clientIds.length > 0) {
      params.clientIds = currentFilters.clientIds;
    }
    if (
      currentFilters.hasClient !== undefined &&
      currentFilters.hasClient !== null
    ) {
      params.hasClient = String(currentFilters.hasClient);
    }

    dispatch(getUsers({ params }));
  }, [dispatch, currentFilters]);

  const selectedUsersData = filteredData.filter(user =>
    selectedUsers.has(user.id)
  );

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (currentFilters.roles.length > 0) count++;
    if (currentFilters.teamIds.length > 0) count++;
    if (currentFilters.clientIds.length > 0) count++;
    if (currentFilters.hasClient !== undefined) count++;
    return count;
  }, [currentFilters]);

  // Filter handlers
  const handleOpenFilters = () => setIsFilterDrawerOpen(true);
  const handleCloseFilters = () => setIsFilterDrawerOpen(false);

  const handleApplyFilters = () => {
    const formValues = getValues();

    // ControlledAutocomplete stores raw values, not option objects
    const filters: UserAdminFiltersType = {
      roles: formValues.roles || [],
      teamIds: formValues.teamIds || [],
      clientIds: formValues.clientIds || [],
      hasClient:
        formValues.hasClient !== null && formValues.hasClient !== undefined
          ? formValues.hasClient === 'true'
          : undefined,
    };

    dispatch(setFilters(filters));
    handleCloseFilters();
  };

  const handleResetFilters = () => {
    resetForm({
      roles: [],
      teamIds: [],
      clientIds: [],
      hasClient: null,
    });
    dispatch(resetFilters());
    handleCloseFilters();
  };

  return (
    <>
      <AdminToolSkeleton
        title="User Administration"
        toolDescription="Manage Reema user accounts, team membership and client access here."
        buttonContent="Create User"
        onClick={handleCreateUser}
      >
        <TextField
          label="Search by Name"
          variant="outlined"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          fullWidth
          margin="normal"
          sx={{ backgroundColor: '#ffffff' }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            mt: 2,
            mb: 2,
          }}
        >
          <BulkUserOperationsToolbar
            selectedUsers={selectedUsersData}
            onBulkOperation={handleBulkOperation}
            isLoading={isBulkOperationLoading}
            onClearSelection={handleClearSelection}
          />
          <UserAdminFilters
            onOpenFilters={handleOpenFilters}
            activeFilterCount={activeFilterCount}
          />
        </Box>
        <IndexChildContainer>
          <PaginatedTable {...USER_ADMIN_TABLE_PROPS} />
        </IndexChildContainer>
      </AdminToolSkeleton>
      <UserAdminFilterDrawer
        open={isFilterDrawerOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        control={control}
        teams={teamList}
        clients={clientList}
      />
    </>
  );
};
